import { Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

@Directive({ selector: '[appCountUp]', standalone: true })
export class CountUpDirective implements OnChanges, OnDestroy {
  @Input() appCountUp = 0;
  @Input() countUpFormat: 'integer' | 'decimal' | 'currency' | 'percent' = 'integer';
  @Input() countUpDuration = 1250;

  private frameId: number | null = null;
  private renderedValue = 0;

  constructor(private readonly element: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['appCountUp']) return;
    this.animateTo(Number(this.appCountUp) || 0);
  }

  ngOnDestroy(): void {
    if (this.frameId !== null) cancelAnimationFrame(this.frameId);
  }

  private animateTo(target: number): void {
    if (this.frameId !== null) cancelAnimationFrame(this.frameId);
    const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduceMotion || this.countUpDuration <= 0) {
      this.renderedValue = target;
      this.render(target);
      return;
    }
    const startValue = this.renderedValue;
    const startedAt = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / this.countUpDuration);
      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      this.renderedValue = startValue + (target - startValue) * eased;
      this.render(this.renderedValue);
      if (progress < 1) this.frameId = requestAnimationFrame(tick);
      else this.frameId = null;
    };
    this.frameId = requestAnimationFrame(tick);
  }

  private render(value: number): void {
    const options: Intl.NumberFormatOptions = this.countUpFormat === 'currency'
      ? { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : this.countUpFormat === 'decimal'
        ? { minimumFractionDigits: 1, maximumFractionDigits: 1 }
        : { maximumFractionDigits: 0 };
    const formatted = new Intl.NumberFormat('es-SV', options).format(value);
    this.element.nativeElement.textContent = this.countUpFormat === 'percent' ? `${formatted}%` : formatted;
    this.element.nativeElement.setAttribute('aria-label', this.formatFinal(Number(this.appCountUp) || 0));
  }

  private formatFinal(value: number): string {
    if (this.countUpFormat === 'currency') return this.formatCurrency(value);
    const formatted = new Intl.NumberFormat('es-SV', {
      minimumFractionDigits: this.countUpFormat === 'decimal' ? 1 : 0,
      maximumFractionDigits: this.countUpFormat === 'decimal' ? 1 : 0,
    }).format(value);
    return this.countUpFormat === 'percent' ? `${formatted}%` : formatted;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(value);
  }
}
